/**
 * Placeholders available in message templates (spec §9). Mirrors the context assembled by the
 * API's MessageDataBuilder; keep the two in sync when a variable is added.
 */

export interface TemplateVariable {
  name: string;
  description: string;
}

export interface TemplateVariableGroup {
  group: string;
  variables: TemplateVariable[];
}

export const TEMPLATE_VARIABLES: TemplateVariableGroup[] = [
  {
    group: 'Position',
    variables: [
      { name: 'position.requestNo', description: 'Portal request number' },
      { name: 'position.role', description: 'Role title' },
      { name: 'position.stage', description: 'Stage code' },
      { name: 'position.workModelText', description: 'Remote / Hybrid / On-site' },
      { name: 'position.startDateText', description: 'Start date or "on request"' },
      { name: 'position.languagesText', description: 'Required languages, " · " separated' },
      { name: 'position.location', description: 'Location text' },
      { name: 'position.workload', description: 'Workload text' },
      { name: 'position.jdReference', description: 'DC-J-… reference' },
      {
        name: 'position.intermediary.name',
        description: 'Intermediary (only allowed in the intermediary template)',
      },
    ],
  },
  {
    group: 'Candidate',
    variables: [
      { name: 'candidate.candidateId', description: 'DC-C-… ID' },
      { name: 'candidate.displayName', description: '"First S." or "Candidate DC-C-…"' },
      { name: 'candidate.label', description: 'Working label' },
      { name: 'candidate.headline', description: 'Headline' },
      { name: 'candidate.location', description: 'City, country' },
      { name: 'candidate.availableFromText', description: 'Availability or "on request"' },
      { name: 'candidate.supplier.name', description: 'Supplier name' },
    ],
  },
  {
    group: 'Submission',
    variables: [
      { name: 'submission.status', description: 'Status code' },
      { name: 'submission.clientRateEur', description: 'Client rate (EUR/h)' },
      { name: 'submission.clientRateNotes', description: 'Rate notes' },
      { name: 'submission.candidateRateText', description: '"65 EUR/h" or "not stated"' },
      { name: 'submission.sentOnText', description: 'Sent on (date)' },
      { name: 'submission.notes', description: 'Full notes' },
    ],
  },
  {
    group: 'Job description',
    variables: [
      { name: 'jd.reference', description: 'DC-J-… or "(no JD yet)"' },
      { name: 'jd.hasJd', description: 'true when a JD version exists (for {{#if}})' },
      { name: 'jd.version', description: 'Version number' },
      { name: 'jd.lang', description: 'EN / DE' },
    ],
  },
  {
    group: 'Recipient partner',
    variables: [
      { name: 'partner.name', description: 'Partner name' },
      { name: 'partner.shortCode', description: 'Short code' },
      { name: 'partner.contactPerson', description: 'Contact person' },
      { name: 'partner.contactFirstName', description: 'First name of the contact, or "there"' },
      { name: 'partner.email', description: 'Contact e-mail' },
      { name: 'otherPartners', description: 'Other briefed suppliers (list; use with {{#each}})' },
    ],
  },
  {
    group: 'Rates',
    variables: [
      { name: 'rates.ceilingText', description: '"up to € 60 / h" or "on request"' },
      { name: 'rates.supplierCeilingDisplay', description: 'Supplier ceiling (number)' },
      { name: 'rates.clientRateH', description: 'Client rate per hour' },
      { name: 'rates.marginPct', description: 'Effective margin' },
    ],
  },
  {
    group: 'Text blocks',
    variables: [
      { name: 'languagesText', description: 'All candidate languages with levels' },
      { name: 'languages', description: 'Language list (language, level, text) for {{#each}}' },
      { name: 'fit', description: '"Why it fits" from the submission notes' },
      { name: 'checkInInterview', description: '"What to check" from the notes or open follow-ups' },
      { name: 'openQuestions', description: 'Open follow-ups as a list for {{#each}}' },
      { name: 'rateJustification', description: 'One sentence when the rate differs from other submissions' },
      { name: 'signature', description: 'Contact name, role, e-mail and phone from the brand settings' },
      { name: 'company.name', description: 'Company name' },
      { name: 'company.website', description: 'Website' },
      { name: 'company.email', description: 'Company e-mail' },
      { name: 'todayText', description: "Today's date" },
    ],
  },
];

export const TEMPLATE_BLOCKS: Array<{ syntax: string; description: string }> = [
  { syntax: '{{#if path}}…{{else}}…{{/if}}', description: 'Conditional block; the else part is optional.' },
  { syntax: '{{#unless path}}…{{/unless}}', description: 'Rendered when the value is empty or false.' },
  {
    syntax: '{{#each list}}- {{this}}\n{{/each}}',
    description: 'Loop; inside, {{this}}, {{this.key}}, {{@index}}, {{@first}} and {{@last}} are available.',
  },
];
